import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { useCreateCapexPlan } from '../../hooks/useCapexQueries'
import { getDetailedErrorMessage } from '../../utils/error-utils'

const CURRENT_YEAR = 2026

interface CreatePlanDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  org: string
  assetUid: string
}

export function CreatePlanDialog({ open, onOpenChange, org, assetUid }: CreatePlanDialogProps) {
  const { t } = useTranslation('capex')
  const [name, setName] = useState('')
  const [horizonStart, setHorizonStart] = useState(CURRENT_YEAR)
  const [horizonEnd, setHorizonEnd] = useState(CURRENT_YEAR + 10)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useCreateCapexPlan(org, assetUid)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!name.trim()) {
      newErrors.name = t('create_plan_dialog.error_name_required')
    }
    if (horizonEnd <= horizonStart) {
      newErrors.horizon = t('create_plan_dialog.error_end_after_start')
    }
    if (horizonEnd - horizonStart > 30) {
      newErrors.horizon = t('create_plan_dialog.error_range_too_large')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        horizon_start: horizonStart,
        horizon_end: horizonEnd,
      })
      onOpenChange(false)
      setName('')
      setHorizonStart(CURRENT_YEAR)
      setHorizonEnd(CURRENT_YEAR + 10)
      setErrors({})
    } catch {
      // error handled by mutation state
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-plan-dialog">
        <DialogHeader>
          <DialogTitle>{t('create_plan_dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="plan-name">{t('create_plan_dialog.field_name')}</Label>
            <Input
              id="plan-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('create_plan_dialog.field_name_placeholder')}
              data-testid="create-plan-name"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Horizon */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="horizon-start">{t('create_plan_dialog.field_horizon_start')}</Label>
              <Input
                id="horizon-start"
                type="number"
                value={horizonStart}
                onChange={(e) => setHorizonStart(Number(e.target.value))}
                data-testid="create-plan-horizon-start"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="horizon-end">{t('create_plan_dialog.field_horizon_end')}</Label>
              <Input
                id="horizon-end"
                type="number"
                value={horizonEnd}
                onChange={(e) => setHorizonEnd(Number(e.target.value))}
                data-testid="create-plan-horizon-end"
              />
            </div>
          </div>
          {errors.horizon && (
            <p className="text-sm text-destructive">{errors.horizon}</p>
          )}

          {/* Surface backend failures (e.g. 409 — an active plan already
              exists for this asset). Without this the dialog silently did
              nothing on error. */}
          {createMutation.isError && (
            <div
              className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive"
              data-testid="create-plan-error"
            >
              {getDetailedErrorMessage(createMutation.error) || t('create_plan_dialog.error_generic')}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('create_plan_dialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="create-plan-submit"
          >
            {createMutation.isPending
              ? t('create_plan_dialog.submitting')
              : t('create_plan_dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
